import { useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../../store';
import {
  syncChatsFromServer,
  addMessageToChat,
  updateChat,
  removeChat,
  setOnlineStatus,
  removePlaceholderMessages
} from '../../chatSlice';
import { GlobalChatStreamService, GlobalChatUpdate } from '../utils/globalChatStreamService';
import { serverMessageToLocal } from '../types/chat';
import { debounce } from '../utils/debounce';

export const useGlobalChatStream = () => {
  const dispatch = useDispatch<AppDispatch>();

  // Используем отдельные селекторы для избежания создания нового объекта
  const isOnline = useSelector((state: RootState) => state.chat.isOnline);
  const isAuthenticated = useSelector((state: RootState) => !!(state.user && state.user.user));

  // Создаем стабильные ссылки на действия
  const syncChatsFromServerAction = useCallback(() => (dispatch as any)(syncChatsFromServer()), [dispatch]);
  const addMessageToChatAction = useCallback((payload: { chatId: string; message: any }) => dispatch(addMessageToChat(payload)), [dispatch]);
  const updateChatAction = useCallback((payload: { id: string; updates: any }) => dispatch(updateChat(payload)), [dispatch]);
  const removeChatAction = useCallback((chatId: string) => dispatch(removeChat(chatId)), [dispatch]);
  const setOnlineStatusAction = useCallback((isOnline: boolean) => dispatch(setOnlineStatus(isOnline)), [dispatch]);
  const streamServiceRef = useRef<GlobalChatStreamService | null>(null);

  // Debounced версия синхронизации для предотвращения избыточных запросов
  const debouncedSyncChats = useCallback(
    debounce(() => {
      syncChatsFromServerAction();
    }, 500), // Задержка 0.5 секунды для глобальных обновлений
    [syncChatsFromServerAction]
  );

  // Обработка обновлений из глобального потока
  const handleChatUpdate = useCallback((update: GlobalChatUpdate) => {
    // console.log('Получено глобальное обновление чата:', update);

    switch (update.event) {
      case 'chat_created':
      case 'chat_updated':
        // При создании или обновлении чата - синхронизируем все чаты (временно отключено)
        // debouncedSyncChats();
        break;

      case 'chat_deleted':
        if (update.data?.id) {
          removeChatAction(update.data.id.toString());
        }
        break;

      case 'message_added':
        if (update.data?.chat_id && update.data?.message) {
          const localMessage = serverMessageToLocal(update.data.message);
          if (localMessage.type === 'audio') {
            dispatch(removePlaceholderMessages({ chatId: update.data.chat_id.toString() }));
          }
          addMessageToChatAction({
            chatId: update.data.chat_id.toString(),
            message: localMessage
          });
        }
        break;

      default:
        console.warn('Неизвестное событие глобального потока:', update.event);
    }
  }, [removeChatAction, addMessageToChatAction]); // Убираем debouncedSyncChats из зависимостей

  // Обработка ошибок потока
  const handleStreamError = useCallback((error: any) => {
    console.error('Ошибка глобального потока чатов:', error);
    setOnlineStatusAction(false);
  }, [setOnlineStatusAction]);

  // Обработка heartbeat
  const handleHeartbeat = useCallback(() => {
    // Обновляем статус онлайн при получении heartbeat
    if (!isOnline) {
      setOnlineStatusAction(true);
    }
  }, [setOnlineStatusAction, isOnline]);

  // Управление потоком
  useEffect(() => {
    // Подключаемся только если пользователь аутентифицирован и онлайн
    if (isAuthenticated && isOnline && !streamServiceRef.current) {
      streamServiceRef.current = new GlobalChatStreamService(
        handleChatUpdate,
        handleStreamError,
        handleHeartbeat
      );

      streamServiceRef.current.start();
    }

    // Отключаемся если пользователь не аутентифицирован или офлайн
    if ((!isAuthenticated || !isOnline) && streamServiceRef.current) {
      streamServiceRef.current.stop();
      streamServiceRef.current = null;
    }

    return () => {
      if (streamServiceRef.current) {
        streamServiceRef.current.stop();
        streamServiceRef.current = null;
      }
    };
  }, [isAuthenticated, isOnline, handleChatUpdate, handleStreamError, handleHeartbeat]);

  const isConnected = streamServiceRef.current?.isConnected() || false;

  return {
    isConnected,
    startStream: () => streamServiceRef.current?.start(),
    stopStream: () => streamServiceRef.current?.stop(),
    updateToken: () => streamServiceRef.current?.updateToken()
  };
};
